import { v4 as uuid } from 'uuid';
import EventBus from '@/factories/eventBus';
import PagesResource from '@/store/resource/pages';
import ReportsResource from '@/store/resource/reports';
import {
  migrateRowColContent,
  normalizeTracks,
  packLayoutColumns,
  setBlockSpan,
} from '@/components/pages/layoutModel';
import { pageNodesForPage } from '@/components/pages/pageBuilderContract';
import {
  HOME_SLUG,
  asRecordList,
  asTemplateCatalog,
  catalogTeamForRecords,
  findRecordBySlug,
  recordSlug,
} from '@/components/pages/pageTree';

const pagesResource = new PagesResource();
const reportsResource = new ReportsResource();
let frameScheduled = false;
let catalogFetchSeq = 0;

function workingTeamId(state, rootGetters) {
  const mainTeamId = rootGetters?.['teams/all']?.find(
    (team) => team.name === 'main_team'
  )?.id;
  return catalogTeamForRecords(
    state.recordsById,
    rootGetters?.['users/userTeam'] || state.catalogTeamId || '',
    mainTeamId
  );
}

function rootKeyedChildren(childrenByParent, migratedPageId, pageId) {
  const from = `root_${migratedPageId || ''}`;
  const to = `root_${pageId}`;
  const next = { ...(childrenByParent || {}) };
  if (from === to || !(from in next)) return next;
  next[to] = next[from];
  delete next[from];
  return next;
}

function packedLayouts(siblings, tracksByBreakpoint, initial = new Map()) {
  const nextLayouts = new Map(initial);
  const byId = new Map(siblings.map((node) => [node.id, node]));
  const tracks = normalizeTracks(tracksByBreakpoint);

  ['tablet', 'desktop'].forEach((breakpoint) => {
    const placed = siblings.map((node) => ({
      ...node,
      layout: nextLayouts.get(node.id) || node.layout,
    }));
    packLayoutColumns(placed, breakpoint, tracks[breakpoint]).forEach(
      ({ id, previousColumn, placement }) => {
        if (previousColumn === placement.column) return;
        const current = nextLayouts.get(id) || byId.get(id).layout || {};
        nextLayouts.set(id, { ...current, [breakpoint]: placement });
      }
    );
  });

  return nextLayouts;
}

export default {
  // ============================================
  // API actions (CRUD for page records)
  // ============================================

  /**
   * Fetch all page records from API.
   * Stores records but does NOT load page content into memory.
   */
  async fetchRecords({ commit, rootGetters }) {
    const teamId = rootGetters?.['users/userTeam'] || '';
    const token = ++catalogFetchSeq;
    commit('beginCatalogFetch', { teamId, token });
    try {
      const { ok, data } = await pagesResource.get();
      if (ok) {
        commit('setRecords', {
          records: asRecordList(data),
          teamId,
          token,
        });
      }
      return { ok: !!ok, data, teamId, token };
    } catch {
      return { ok: false, data: null, teamId, token };
    } finally {
      commit('finishCatalogFetch', { token });
    }
  },

  /**
   * Fetch a single page record by ID.
   * Stores record but does NOT load page content into memory.
   */
  async fetchRecord({ commit, state, rootGetters }, { recordId }) {
    const teamId = rootGetters?.['users/userTeam'] || state.catalogTeamId || '';
    commit('setLoading', true);
    try {
      const { ok, data } = await pagesResource.get(recordId);
      if (ok && data) {
        commit('setRecord', { record: data, teamId });
        if (state.recordsById[data.id] !== data) {
          return { ok: false, data };
        }
        return { ok: true, data };
      }
      return { ok: !!ok, data };
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Resolve a report tile. Empty (not zero) when the report engine is down.
   */
  async loadTile(_, spec) {
    if (!spec?.report && !spec?.saved) {
      return { empty: true, reason: 'report', report: null };
    }
    try {
      const { ok, data } = await reportsResource.get('tile', {
        report: spec.report || undefined,
        saved: spec.saved || undefined,
        shape: spec.shape || 'stat',
        prereq: spec.prereq || undefined,
        prompt: spec.prompt || undefined,
        label: spec.label || undefined,
        ...(spec.params || {}),
      });
      if (ok && data) return data;
    } catch {
      /* report engine or SingleStore down — empty, not zero */
    }
    return { empty: true, reason: 'unavailable', report: null };
  },

  async fetchTemplates({ commit, state }) {
    if (Object.keys(state.templatesBySection || {}).length) {
      return { ok: true, data: state.templatesBySection };
    }
    const { ok, data } = await pagesResource.get('templates');
    if (ok && data) {
      commit('setTemplates', asTemplateCatalog(data));
    }
    return { ok, data };
  },

  async ensureTree({ state, dispatch, rootGetters }) {
    const teamId = rootGetters?.['users/userTeam'] || '';
    if (!teamId && !rootGetters?.['teams/all']?.length) {
      await dispatch('teams/load', { checkLoaded: true }, { root: true });
    }
    const teamChanged = !!(
      teamId &&
      state.catalogTeamId &&
      teamId !== state.catalogTeamId
    );
    if (!state.treeLoaded || teamChanged) {
      await dispatch('fetchRecords');
    }
    await dispatch('fetchTemplates');
  },

  /**
   * Ask Cy tree mutations. Configuration only — never a layout.
   */
  async mutate({ dispatch, rootGetters }, input = {}) {
    if (input.op === 'movePage' && !rootGetters?.['users/isAdmin']) {
      return {
        ok: false,
        data: { reason: 'admin' },
        message: 'You do not have access to move that page',
      };
    }
    const team = input.team || rootGetters?.['users/userTeam'] || '';
    const payload = { ...input };
    if (team && !payload.team) payload.team = team;

    try {
      const result = await pagesResource.create('mutate', payload);
      if (!result?.ok) {
        return {
          ok: false,
          data: result?.data || {},
          message: result?.data?.message || 'Unable to mutate page',
        };
      }
      const data = result.data || {};
      await dispatch('onPageMutation', {
        op: payload.op,
        slug: data.slug,
        pageId: data.pageId,
        id: data.id,
        created: data.created,
      });
      return { ok: true, data };
    } catch {
      return { ok: false, data: {}, message: 'Unable to mutate page' };
    }
  },

  async onPageMutation({ state, dispatch, rootGetters }, event = {}) {
    const slug = event.slug ? recordSlug({ slug: event.slug }) : '';
    const teamId = workingTeamId(state, rootGetters);
    const payload = {
      op: event.op || null,
      slug: slug || null,
      pageId: event.pageId || null,
      id: event.id || null,
      created: event.created,
    };
    const existing = slug
      ? findRecordBySlug(state.recordsById, slug, teamId)
      : null;
    const loaded = existing
      ? Object.values(state.pagesById).find(
          (page) => page.recordId === existing.id
        )
      : null;

    const catalog = await dispatch('fetchRecords');
    const present = slug
      ? !!findRecordBySlug(state.recordsById, slug, teamId)
      : false;
    const ok = !!(catalog?.ok && present);

    if (ok) {
      if (loaded && state.pagesById[loaded.id]) {
        await dispatch('deletePage', { pageId: loaded.id });
      }
      EventBus.$emit('pageMutation', payload);
    } else if (slug) {
      EventBus.$emit('pageMutationRefreshError', payload);
    }

    return {
      ok,
      ...payload,
      reason: ok ? null : 'refresh',
    };
  },

  async openBySlug({ state, commit, dispatch, rootGetters }, slug) {
    const key = recordSlug({ slug: slug || HOME_SLUG });
    let record = findRecordBySlug(
      state.recordsById,
      key,
      workingTeamId(state, rootGetters)
    );
    if (!record && !state.treeLoaded) {
      const catalog = await dispatch('fetchRecords');
      record = findRecordBySlug(
        state.recordsById,
        key,
        workingTeamId(state, rootGetters)
      );
      if (!record && !catalog?.ok) {
        return { ok: false, pageId: null, reason: 'error' };
      }
    }
    if (!record) {
      return {
        ok: false,
        pageId: null,
        reason: state.treeLoaded ? 'missing' : 'error',
      };
    }

    const existing = Object.values(state.pagesById).find(
      (page) => page.recordId === record.id
    );
    if (existing) {
      commit('setActivePage', existing.id);
      return { ok: true, pageId: existing.id, reason: null };
    }

    if (!record.page?.nodes) {
      const fetched = await dispatch('fetchRecord', { recordId: record.id });
      if (!fetched?.ok) {
        return { ok: false, pageId: null, reason: 'error' };
      }
      record = state.recordsById[record.id];
    }
    if (!record?.page) {
      return { ok: false, pageId: null, reason: 'error' };
    }
    const pageId = await dispatch('loadRecordPage', { recordId: record.id });
    if (!pageId) {
      return { ok: false, pageId: null, reason: 'error' };
    }
    return { ok: true, pageId, reason: null };
  },

  /**
   * Load a record's page content into memory for rendering.
   * Call this after fetchRecord when you want to actually render the page.
   */
  loadRecordPage({ commit, state }, { recordId }) {
    const record = state.recordsById[recordId];
    if (!record || !record.page) return null;

    const pageData = migrateRowColContent(record.page || {});
    const recordPageId = pageData.pageId || pageData.id || null;
    const existing = Object.values(state.pagesById).find(
      (page) => page.recordId === recordId || page.id === recordPageId
    );

    // A stream shell may already be linked to this recordId. It is not
    // a hydrated Mongo page — still insert persisted nodes.
    if (existing && !existing.streamShell) {
      commit('setActivePage', existing.id);
      return existing.id;
    }

    const pageId = existing?.id || recordPageId || uuid();

    if (existing) {
      commit('hydrateStreamPage', {
        pageId,
        recordId,
        meta: pageData.meta || {},
      });
    } else {
      commit('createPage', {
        pageId,
        meta: pageData.meta || {},
        recordId,
      });
    }

    pageNodesForPage(pageData.nodes, pageId).forEach((node) => {
      if (state.nodesById[node.id]) return;
      commit('insertNode', { node });
    });

    // migrateRowColContent keeps stored sibling order when nodes
    // omit afterId, and sorts by afterId when that chain exists.
    commit('restoreChildrenOrder', {
      childrenByParent: rootKeyedChildren(
        pageData.childrenByParent,
        pageData.pageId,
        pageId
      ),
    });

    Object.values(state.nodesById)
      .filter((node) => node.pageId === pageId && node.nodeType === 'layout')
      .forEach((layout) => {
        const siblings = (state.childrenByParent[layout.id] || [])
          .map((id) => state.nodesById[id])
          .filter(Boolean);
        packedLayouts(siblings, layout.props?.tracks).forEach(
          (placement, id) => {
            commit('patchNodeLayout', {
              nodeId: id,
              layout: placement,
              replace: true,
            });
          }
        );
      });

    commit('setActivePage', pageId);
    return pageId;
  },

  /**
   * Save a page to the API.
   * Creates a new record if no recordId exists, otherwise updates.
   */
  async savePage(
    { commit, state, getters, rootGetters },
    { pageId, name = 'Untitled', pid = null, skipErrorModal = false }
  ) {
    const page = state.pagesById[pageId];
    if (!page) return { ok: false, error: 'Page not found' };

    commit('setSaving', true);
    try {
      // Serialize page content
      const serialized = getters.serializePage(pageId);
      if (!serialized) return { ok: false, error: 'Failed to serialize page' };

      const payload = {
        name,
        pid,
        page: serialized,
      };

      let result;
      const requestConfig = skipErrorModal ? { skipErrorModal: true } : null;
      if (page.recordId) {
        // Update existing record
        payload.id = page.recordId;
        result = requestConfig
          ? await pagesResource.update(payload, requestConfig)
          : await pagesResource.update(payload);
      } else {
        // Create new record
        result = requestConfig
          ? await pagesResource.create(payload, requestConfig)
          : await pagesResource.create(payload);
      }

      if (result.ok && result.data) {
        commit('setRecord', {
          record: result.data,
          teamId: rootGetters?.['users/userTeam'] || state.catalogTeamId || '',
        });
        // Link page to record if newly created
        if (!page.recordId && result.data.id) {
          commit('setPageRecordId', { pageId, recordId: result.data.id });
        }
      }

      return result;
    } finally {
      commit('setSaving', false);
    }
  },

  /**
   * Delete a page record from the API.
   * Also removes local page content from memory.
   */
  async deleteRecord({ commit, state, dispatch }, { recordId }) {
    const record = state.recordsById[recordId];
    if (!record) return { ok: false, error: 'Record not found' };

    commit('setLoading', true);
    try {
      const result = await pagesResource.delete(recordId);

      if (result.ok) {
        // Find and delete local page if loaded
        const pageEntry = Object.values(state.pagesById).find(
          (p) => p.recordId === recordId
        );
        if (pageEntry) {
          dispatch('deletePage', { pageId: pageEntry.id });
        }
        commit('removeRecord', { recordId });
      }

      return result;
    } finally {
      commit('setLoading', false);
    }
  },

  // ============================================
  // Local page actions (in-memory only)
  // ============================================

  /**
   * Create a new empty page in memory (not saved to API).
   * Use savePage() to persist to backend.
   */
  createPage({ commit }, { meta = {} } = {}) {
    const pageId = uuid();
    commit('createPage', { pageId, meta, recordId: null });
    commit('setActivePage', pageId);
    return pageId;
  },

  /**
   * Delete a page from memory.
   * Does NOT delete from API - use deleteRecord for that.
   */
  deletePage({ commit, state }, { pageId }) {
    // Recursively remove all nodes for this page
    const nodeIds = Object.keys(state.nodesById).filter(
      (id) => state.nodesById[id].pageId === pageId
    );
    nodeIds.forEach((nodeId) => commit('removeNode', { nodeId }));

    commit('deletePage', { pageId });
  },

  /**
   * Load page content from a JSON object (for demos, imports, etc.).
   * Creates a local-only page - use savePage() to persist.
   */
  loadPage({ commit }, { json }) {
    const migrated = migrateRowColContent(json || {});
    const pageId = migrated.pageId || migrated.id || uuid();
    commit('createPage', { pageId, meta: migrated.meta || {}, recordId: null });

    pageNodesForPage(migrated.nodes, pageId).forEach((node) => {
      commit('insertNode', { node });
    });
    commit('restoreChildrenOrder', {
      childrenByParent: rootKeyedChildren(
        migrated.childrenByParent,
        migrated.pageId,
        pageId
      ),
    });

    commit('setActivePage', pageId);
    return pageId;
  },

  /**
   * Join a page-builder stream to a journey page already in memory,
   * or open a local shell so streamed inserts have a pageId root.
   * The shell is not a hydrated Mongo page — loadRecordPage still
   * fills persisted nodes even when recordId is already linked.
   */
  ensureStreamPage({ commit, state }, { pageId, recordId = null }) {
    if (!pageId) return null;
    if (!state.pagesById[pageId]) {
      commit('createPage', { pageId, recordId, streamShell: true });
    } else if (recordId && !state.pagesById[pageId].recordId) {
      commit('setPageRecordId', { pageId, recordId });
    }
    return pageId;
  },

  /**
   * Set a layout child's designed desktop span and persist the page.
   */
  async setNodeSpan({ commit, state, getters, dispatch }, { nodeId, span }) {
    const node = state.nodesById[nodeId];
    if (!node) return { ok: false, error: 'Node not found' };

    const parent = node.parentId ? state.nodesById[node.parentId] : null;
    const initial = new Map([
      [nodeId, setBlockSpan(node.layout, span, parent?.props?.tracks)],
    ]);
    const nextLayouts =
      parent?.nodeType === 'layout'
        ? packedLayouts(
            (state.childrenByParent[parent.id] || [])
              .map((id) => state.nodesById[id])
              .filter(Boolean),
            parent.props?.tracks,
            initial
          )
        : initial;

    const changed = [...nextLayouts].map(([id, layout]) => {
      const previous = state.nodesById[id].layout;
      commit('patchNodeLayout', { nodeId: id, layout, replace: true });
      return { id, previous, updated: state.nodesById[id].layout };
    });

    const record = getters.recordForPage(node.pageId);
    if (!record) return { ok: true, saved: false };

    let result;
    try {
      result = await dispatch('savePage', {
        pageId: node.pageId,
        name: record.name || 'Untitled',
        pid: record.pid || null,
      });
    } catch (error) {
      result = {
        ok: false,
        error: error?.message || 'Page could not be saved',
      };
    }
    if (!result?.ok) {
      changed.forEach(({ id, previous, updated }) => {
        if (state.nodesById[id]?.layout !== updated) return;
        commit('patchNodeLayout', {
          nodeId: id,
          layout: previous,
          replace: true,
        });
      });
    }
    return result || { ok: false, error: 'Page could not be saved' };
  },

  /**
   * Apply a planned drag-and-drop and persist the page; rolls back when the save fails.
   */
  async dropNodes({ commit, state, getters, dispatch }, updates = []) {
    const planned = updates.filter((update) => state.nodesById[update?.id]);
    if (!planned.length) return { ok: false, error: 'Node not found' };

    const pageId = state.nodesById[planned[0].id].pageId;
    const parentKey = (parentId) => parentId || `root_${pageId}`;
    const before = new Map();
    const touched = new Set();
    planned.forEach(({ id, newParentId }) => {
      const node = state.nodesById[id];
      if (!before.has(id)) {
        before.set(id, { parentId: node.parentId, layout: node.layout });
      }
      touched.add(parentKey(node.parentId));
      if (newParentId !== undefined) touched.add(parentKey(newParentId));
    });
    const order = {};
    touched.forEach((key) => {
      if (state.childrenByParent[key]) {
        order[key] = [...state.childrenByParent[key]];
      }
    });

    planned.forEach((update) => {
      if (update.op === 'move') {
        commit('moveNode', {
          nodeId: update.id,
          newParentId: update.newParentId,
          afterId: update.afterId,
        });
      } else if (update.layout !== undefined) {
        commit('patchNodeLayout', { nodeId: update.id, layout: update.layout });
      }
    });

    const record = getters.recordForPage(pageId);
    if (!record) return { ok: true, saved: false };

    let result;
    try {
      result = await dispatch('savePage', {
        pageId,
        name: record.name || 'Untitled',
        pid: record.pid || null,
        skipErrorModal: true,
      });
    } catch (error) {
      result = {
        ok: false,
        error: error?.message || 'Page could not be saved',
      };
    }
    if (!result?.ok) {
      before.forEach(({ parentId, layout }, id) => {
        if (!state.nodesById[id]) return;
        if (state.nodesById[id].parentId !== parentId) {
          commit('moveNode', { nodeId: id, newParentId: parentId });
        }
        commit('patchNodeLayout', {
          nodeId: id,
          layout: layout ?? null,
          replace: true,
        });
      });
      commit('restoreChildrenOrder', { childrenByParent: order });
    }
    return result || { ok: false, error: 'Page could not be saved' };
  },

  /**
   * Check or uncheck one todo item and persist it; rolls back when the save fails.
   */
  async checkTask({ commit, state, getters }, { nodeId, index, checked }) {
    const node = state.nodesById[nodeId];
    const previous = node?.props?.items;
    if (!Array.isArray(previous) || !previous[index]) {
      return { ok: false, error: 'Task not found' };
    }

    const items = previous.map((item, i) => {
      if (i !== index) return item;
      return typeof item === 'string'
        ? { text: item, checked }
        : { ...item, checked };
    });
    commit('patchNodeProps', { nodeId, props: { items } });

    const record = getters.recordForPage(node.pageId);
    if (!record) return { ok: true, saved: false };

    let ok = false;
    try {
      const result = await pagesResource.create('mutate', {
        op: 'checkTask',
        id: record.id,
        team: record.team,
        nodeId,
        index,
        checked,
      });
      ok = !!result?.ok;
    } catch {
      ok = false;
    }
    if (!ok && state.nodesById[nodeId]?.props?.items === items) {
      commit('patchNodeProps', { nodeId, props: { items: previous } });
    }
    return { ok };
  },

  // Single update - queued for batching
  applyUpdate({ commit, dispatch }, update) {
    commit('queueUpdate', update);
    dispatch('scheduleFlush');
  },

  // Batch updates
  applyUpdatesBatch({ commit, dispatch }, updates) {
    updates.forEach((update) => commit('queueUpdate', update));
    dispatch('scheduleFlush');
  },

  // Animation frame batching (per PRD section 6.2)
  scheduleFlush({ state, commit }) {
    if (frameScheduled) return;
    frameScheduled = true;

    requestAnimationFrame(() => {
      const updates = [...state.updateQueue];
      commit('clearUpdateQueue');
      frameScheduled = false;

      // Coalesce updates to same node
      const byNode = {};
      updates.forEach((u) => {
        if (!byNode[u.id]) byNode[u.id] = [];
        byNode[u.id].push(u);
      });

      // Apply coalesced updates
      Object.entries(byNode).forEach(([nodeId, nodeUpdates]) => {
        nodeUpdates.forEach((update) => {
          if (update.op === 'insert') {
            commit('insertNode', { node: update });
          } else if (
            update.op === 'patch' ||
            update.props ||
            update.layout !== undefined ||
            update.height
          ) {
            if (update.props) {
              commit('patchNodeProps', { nodeId, props: update.props });
            }
            if (update.layout !== undefined) {
              commit('patchNodeLayout', { nodeId, layout: update.layout });
            }
            if (update.height) {
              commit('patchNodeHeight', { nodeId, height: update.height });
            }
          } else if (update.op === 'appendChunk') {
            commit('appendChunk', { nodeId, text: update.text });
          } else if (update.op === 'move') {
            commit('moveNode', {
              nodeId,
              newParentId: update.newParentId,
              afterId: update.afterId,
            });
          } else if (update.op === 'remove') {
            commit('removeNode', { nodeId });
          }
        });
      });
    });
  },

  // Component registration
  registerComponent({ commit }, { nodeType, component }) {
    commit('registerComponent', { nodeType, component });
  },
};
